import { create } from 'zustand';
import type { StocktakeCsvRow } from '../../hooks/useCsvParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep = 'upload' | 'validation' | 'preview' | 'processing' | 'result';

export interface ValidationError {
  row: number;
  sku: string;
  reason: string;
  quantity?: number;
}

export interface StocktakePreviewRow {
  sku: string;
  skuId: string;
  currentStock: number;
  newStock: number;
  difference: number;
  unitCost: number | null;
  costImpact: number | null;
  /** Resolved at validation time (ValidationStep) — the org this row's count
   *  writes to. Always set: an unresolved Owner cell is rejected as an error
   *  before a row ever reaches preview. Lets the submit handler route the
   *  write without re-resolving the Owner cell by bare sku. */
  targetOrganizationId: string;
}

export interface ProcessingResult {
  batchId: string;
  successCount: number;
  failedCount: number;
  validationErrors: ValidationError[];
  executionErrors: string[];
  mode?: 'complete' | 'partial' | 'blocked' | 'network_error';
  /** BLU-711 A2: rows staged for a client's approval instead of written.
   *  Non-empty only at a shared location whose junction is in `review` mode;
   *  every other submission returns an empty array, exactly as before. */
  proposals?: Array<{ proposalId: string; clientOrganizationId: string; lineCount: number }>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface BulkStocktakeUploadState {
  step: WizardStep;
  file: File | null;
  parsedRows: StocktakeCsvRow[];
  /** Original cell text for `parsedRows[i]`, aligned by index. Distinguishes
   *  a truly blank Stock Count cell from junk (both parse to `stock: NaN`). */
  rawRows: Record<string, string>[];
  /** 1-based source line for `parsedRows[i]`, aligned by index. */
  sourceRows: number[];
  validationErrors: ValidationError[];
  previewRows: StocktakePreviewRow[];
  /** Rows that failed validation (renamed from `skippedRows` — that name
   *  read as "not counted", which is a DIFFERENT bucket from "errored"). */
  erroredRows: StocktakeCsvRow[];
  /** Rows with a blank Stock Count cell — not counted, never submitted,
   *  never an error. Surfaced separately so PreviewStep can tell the
   *  operator "N rows were left blank" without conflating it with errors. */
  notCountedRows: StocktakeCsvRow[];
  /** Operator reference / order number for the adjustment-document header. */
  reference: string;
  processing: { total: number; processed: number; failed: number };
  result: ProcessingResult | null;
}

interface BulkStocktakeUploadActions {
  setStep: (step: WizardStep) => void;
  setFile: (file: File | null) => void;
  setReference: (reference: string) => void;
  setParsedRows: (rows: StocktakeCsvRow[]) => void;
  setRawRows: (rows: Record<string, string>[]) => void;
  setSourceRows: (rows: number[]) => void;
  setValidationErrors: (errors: ValidationError[]) => void;
  setPreviewRows: (rows: StocktakePreviewRow[]) => void;
  setErroredRows: (rows: StocktakeCsvRow[]) => void;
  setNotCountedRows: (rows: StocktakeCsvRow[]) => void;
  startProcessing: (total: number) => void;
  tickProcessed: () => void;
  tickFailed: () => void;
  setResult: (result: ProcessingResult) => void;
  clearResult: () => void;
  reset: () => void;
}

type BulkStocktakeUploadStore = BulkStocktakeUploadState & BulkStocktakeUploadActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: BulkStocktakeUploadState = {
  step: 'upload',
  file: null,
  parsedRows: [],
  rawRows: [],
  sourceRows: [],
  validationErrors: [],
  previewRows: [],
  erroredRows: [],
  notCountedRows: [],
  reference: '',
  processing: { total: 0, processed: 0, failed: 0 },
  result: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useBulkStocktakeUpload = create<BulkStocktakeUploadStore>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),

  setFile: (file) => set({ file }),

  setReference: (reference) => set({ reference }),

  setParsedRows: (rows) => set({ parsedRows: rows }),

  setRawRows: (rows) => set({ rawRows: rows }),

  setSourceRows: (rows) => set({ sourceRows: rows }),

  setValidationErrors: (errors) => set({ validationErrors: errors }),

  setPreviewRows: (rows) => set({ previewRows: rows }),

  setErroredRows: (rows) => set({ erroredRows: rows }),

  setNotCountedRows: (rows) => set({ notCountedRows: rows }),

  startProcessing: (total) =>
    set({
      step: 'processing',
      processing: { total, processed: 0, failed: 0 },
    }),

  tickProcessed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
      },
    })),

  tickFailed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
        failed: s.processing.failed + 1,
      },
    })),

  setResult: (result) => set({ step: 'result', result }),

  clearResult: () => set({ result: null }),

  reset: () => set(initialState),
}));
